// The API/SDK token-link params that ARE the session: `token` is the bearer (read from the live URL on
// every request, see main.tsx), `lang` drives i18n, `clientId`/`ref` scope the embed. The router keeps
// them across CLIENT navigations via `retainSearchParams` (see __root), but a HARD redirect
// (`window.location`) re-initializes the app from the new URL — so the test-gate / auth redirects must
// carry these forward by hand or the token-bearer is logged out (e.g. opening `/report?token=…` for an
// applicant who hasn't finished the test bounced to a bare `/test/competences`, dropping the session).
const SDK_PARAMS = ['token', 'lang', 'clientId', 'ref'] as const

/**
 * Does THIS document own the persisted (localStorage) query cache for its origin?
 *
 * `localStorage` is per-ORIGIN, but a token link authenticates the document as a DIFFERENT entity than
 * the session that owns the origin's blob — and both of ours are same-origin by construction:
 *   - the API Playground previews (`token-embed.tsx`) embed token links in an iframe inside the API
 *     owner's own signed-in page, and
 *   - "Open in new tab" opens the same token link top-level, still on our origin.
 * So a token document would REHYDRATE the owner's cache before React hydrates (the persister restores in
 * a microtask, the hydration render is scheduled) and clobber the identity-scoped queries its SSR HTML was
 * built from → React discards the subtree (#418) and, worse, the entity's own data gets written back over
 * the owner's blob. `__root.tsx`'s identity guard can't cover this: it fires after `getUser` RESOLVES,
 * which is post-hydration, and it would then stamp the owner's identity tag with the token entity's id.
 *
 * An iframe embed is excluded for the same reason even without a token (the SDK's cross-origin embed has
 * its own storage anyway, so this only bites our own same-origin frames).
 *
 * `isTopLevel` is passed in rather than read here because `window.self === window.top` is a CLIENT-only
 * signal — the server can't know it, so anything gated on this must not change the rendered tree (we make
 * the PERSISTER inert instead, which keeps the React tree identical on both sides).
 */
export function documentOwnsPersistedCache(search: string, isTopLevel: boolean): boolean {
  if (!isTopLevel) return false
  // Truthy, not `has`: a bare `?token=` is not a session (main.tsx sends the Bearer header on truthiness).
  return !new URLSearchParams(search).get('token')
}

// Append the current URL's SDK params to a same-origin redirect target (never overriding ones the target
// already sets). `path` may include a query/hash; returns a path string for `window.location`.
export function withSdkParams(path: string): string {
  if (typeof window === 'undefined') return path
  const url = new URL(path, window.location.origin)
  const current = new URLSearchParams(window.location.search)
  for (const key of SDK_PARAMS) {
    const value = current.get(key)
    if (value && !url.searchParams.has(key)) url.searchParams.set(key, value)
  }
  return `${url.pathname}${url.search}${url.hash}`
}
